import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { toJpeg } from "html-to-image";
import ScreenshotBoard from "../../prototype/mobile-jigsaw/board/ScreenshotBoard";
import {
    BOARD_SIZE,
    PieceVisual,
    pieceVisualRenderSizePx,
} from "../../prototype/mobile-jigsaw/board/PieceVisual";
import { useBoardMatColor } from "../../prototype/mobile-jigsaw/board/useBoardMatColor";
import {
    getImageUrl,
    getStoryTitle,
    getTemplateGrid,
    loadScreenshotJigsaw,
} from "../../prototype/mobile-jigsaw/lib/api";
import { getScreenshotScene } from "../../prototype/mobile-jigsaw/screenshotScenes";
import ScreenshotPlayPanel from "../../prototype/mobile-word-wheel/components/ScreenshotPlayPanel";
import { normalizeWord } from "../../prototype/mobile-word-wheel/lib/gridReveal";
import { loadScreenshotWordWheel } from "../../prototype/mobile-word-wheel/lib/screenshotApi";
import { getScreenshotScene as getWwScreenshotScene } from "../../prototype/mobile-word-wheel/screenshotScenes";

/** Google Play developer page header — store listing / developer branding. */
export const GOOGLE_HEADER_W = 4096;
export const GOOGLE_HEADER_H = 2304;
export const GOOGLE_HEADER_FILE = "puzzle-interact-google-header-4096x2304.jpg";

const SAVE_BAR_H = 88;
const PUBLIC = process.env.PUBLIC_URL || "";
const JIGSAW_APP_ICON = `${PUBLIC}/ad/jigsaw-muse-app-icon-512.png`;
const WORD_WHEEL_APP_ICON = `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`;
/** Ocean puzzle — clear interlocking grid for the Jigsaw Muse card. */
const JIGSAW_PLAY_SCENE_SLUG = "05";
/** Core gameplay screenshot scene for the Word Wheel Quest card. */
const WORD_WHEEL_PLAY_SCENE_SLUG = "02";

const BRAND_STACK =
    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

/** Sliding stays an image card; Jigsaw Muse + Word Wheel use live play surfaces. */
const IMAGE_CARDS = [
    {
        key: "sliding",
        label: "Sliding Puzzle Quest",
        image: `${PUBLIC}/ios-sliding-snapshots/300x650bb_1.webp`,
        appIcon: `${PUBLIC}/ad/sliding-app-icon-2-ref.png`,
        accent: "#0284C7",
        imageFit: "cover",
    },
];

function downloadBlob(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function formatExportError(err) {
    if (!err) return "Failed to download JPEG";
    if (typeof err === "string" && err.trim()) return err;
    const msg = err?.message || err?.error || "";
    if (typeof msg === "string" && msg.trim()) return msg;
    if (err?.type === "error" || err?.target) {
        return "Failed to embed an image for export (likely CORS). Try again.";
    }
    const asText = String(err);
    return asText && asText !== "[object Event]" && asText !== "[object Object]"
        ? asText
        : "Failed to download JPEG";
}

/** html-to-image needs same-origin/data URLs for SVG <image> — convert remote puzzle art. */
async function toEmbeddableDataUrl(url) {
    if (!url || typeof url !== "string") return url;
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    try {
        const res = await fetch(url, { mode: "cors", credentials: "omit", cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read image blob"));
            reader.readAsDataURL(blob);
        });
    } catch {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.naturalWidth || img.width;
                    canvas.height = img.naturalHeight || img.height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        reject(new Error("Canvas unavailable"));
                        return;
                    }
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL("image/jpeg", 0.92));
                } catch (e) {
                    reject(e);
                }
            };
            img.onerror = () => reject(new Error(`Could not load image for export: ${url}`));
            const bust = `cb=${Date.now()}`;
            img.src = url.includes("?") ? `${url}&${bust}` : `${url}?${bust}`;
        });
    }
}

function CardFooter({ appIcon, label }) {
    return (
        <Box
            sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                pt: 8,
                pb: 3.5,
                px: 3.5,
                background:
                    "linear-gradient(180deg, transparent 0%, rgba(15,23,42,0.82) 55%, rgba(15,23,42,0.95) 100%)",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                zIndex: 4,
            }}
        >
            <Box
                component="img"
                src={appIcon}
                alt=""
                title={label}
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "18px",
                    objectFit: "cover",
                    flexShrink: 0,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                    border: "3px solid rgba(255,255,255,0.9)",
                    bgcolor: "#fff",
                }}
            />
            <Typography
                sx={{
                    m: 0,
                    color: "#F8FAFC",
                    fontFamily: BRAND_STACK,
                    fontWeight: 800,
                    fontSize: 40,
                    letterSpacing: "0.02em",
                    lineHeight: 1.1,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

function GenreCard({ label, image, appIcon, accent, imageFit = "cover", iconAsHero = false }) {
    return (
        <Box
            sx={{
                flex: "1 1 0",
                minWidth: 0,
                height: "100%",
                borderRadius: "28px",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
                border: "4px solid rgba(255,255,255,0.92)",
                bgcolor: accent,
            }}
        >
            {iconAsHero ? (
                <>
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: `linear-gradient(160deg, ${accent} 0%, #1E293B 55%, #0F172A 100%)`,
                        }}
                    />
                    <Box
                        component="img"
                        src={image}
                        alt=""
                        sx={{
                            position: "absolute",
                            left: "50%",
                            top: "38%",
                            transform: "translate(-50%, -50%)",
                            width: "72%",
                            maxWidth: 520,
                            aspectRatio: "1 / 1",
                            objectFit: "cover",
                            borderRadius: "22%",
                            display: "block",
                            boxShadow: "0 28px 64px rgba(0,0,0,0.45)",
                            border: "6px solid rgba(255,255,255,0.88)",
                        }}
                    />
                </>
            ) : (
                <Box
                    component="img"
                    src={image}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: imageFit,
                        objectPosition: "center",
                        display: "block",
                    }}
                />
            )}
            <CardFooter appIcon={appIcon} label={label} />
        </Box>
    );
}

/**
 * Live Jigsaw Muse play surface — puzzle title above grid, piece tray below.
 * Tray pieces use the same screen scale as pieces on the board.
 * `exportLite` collapses the board to one full image so html-to-image can serialize it.
 */
function JigsawMusePlayCard({ puzzle, exportLite = false }) {
    const { mat: boardMat, grooveStyle: boardGrooveStyle } = useBoardMatColor();
    const {
        imageUrl,
        grid,
        paths,
        svgPathFallback,
        boardPieceIndices,
        trayPieceIndices,
        puzzleTitle,
        ready,
    } = puzzle;
    const [boardScale, setBoardScale] = useState(1);
    const [squareSide, setSquareSide] = useState(0);
    const boardSlotRef = useRef(null);
    const TRAY_GAP_PX = 40;
    const TRAY_ROW_GAP_PX = 6;
    const PUZZLE_TITLE_GAP_PX = 10;
    const PUZZLE_TITLE_BLOCK_PX = 42; // ~28px font + line
    const TRAY_PAD_Y_PX = 12; // py: 0.75 ≈ 6px * 2

    const cellPx = grid > 0 ? BOARD_SIZE / grid : 0;
    // Same visual size as a piece on the grid (no 0.88 tray shrink).
    const pieceScreenPx =
        cellPx > 0 && boardScale > 0
            ? Math.max(24, Math.round(pieceVisualRenderSizePx(cellPx) * boardScale))
            : 48;
    const trayCirclePx = pieceScreenPx + 4;
    const trayBlockPx = trayCirclePx * 2 + TRAY_ROW_GAP_PX + TRAY_PAD_Y_PX;

    useEffect(() => {
        const el = boardSlotRef.current;
        if (!el) return undefined;
        const sync = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            if (w <= 0 || h <= 0) return;
            const chrome = PUZZLE_TITLE_BLOCK_PX + PUZZLE_TITLE_GAP_PX + trayBlockPx + TRAY_GAP_PX;
            const forBoard = Math.max(0, h - chrome);
            const side = Math.floor(Math.min(w, forBoard));
            setSquareSide((prev) => (prev === side ? prev : side));
        };
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => ro.disconnect();
    }, [ready, trayBlockPx]);

    return (
        <Box
            data-jigsaw-play-card
            sx={{
                flex: "1 1 0",
                minWidth: 0,
                height: "100%",
                borderRadius: "28px",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
                border: "4px solid rgba(255,255,255,0.92)",
                bgcolor: "#c5d5ce",
                display: "flex",
                flexDirection: "column",
                px: 2.5,
                pt: "120px",
                pb: 14,
                boxSizing: "border-box",
            }}
        >
            <Typography
                sx={{
                    m: 0,
                    mb: 1.5,
                    maxWidth: "100%",
                    fontFamily: BRAND_STACK,
                    fontWeight: 800,
                    fontSize: 34,
                    lineHeight: 1.15,
                    letterSpacing: "0.01em",
                    color: "#0F172A",
                    textAlign: "center",
                    textShadow: "0 1px 0 rgba(255,255,255,0.65)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                }}
            >
                JIGSAW MUSE
            </Typography>

            <Box
                sx={{
                    flex: "1 1 0",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    zIndex: 1,
                }}
            >
                {ready ? (
                    <Box
                        ref={boardSlotRef}
                        sx={{
                            flex: "1 1 0",
                            minHeight: 0,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: squareSide > 0 ? squareSide : "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                flexShrink: 0,
                                maxWidth: "100%",
                            }}
                        >
                            <Typography
                                sx={{
                                    m: 0,
                                    mb: `${PUZZLE_TITLE_GAP_PX}px`,
                                    maxWidth: "100%",
                                    fontFamily: BRAND_STACK,
                                    fontWeight: 800,
                                    fontSize: 28,
                                    lineHeight: 1.15,
                                    letterSpacing: "0.01em",
                                    color: "#0F172A",
                                    textAlign: "left",
                                    textShadow: "0 1px 0 rgba(255,255,255,0.65)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                }}
                            >
                                {puzzleTitle || "Puzzle"}
                            </Typography>

                            <Box
                                sx={{
                                    width: squareSide > 0 ? squareSide : "100%",
                                    height: squareSide > 0 ? squareSide : "auto",
                                    aspectRatio: squareSide > 0 ? undefined : "1 / 1",
                                    maxWidth: "100%",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    boxShadow: "0 16px 40px rgba(15,23,42,0.22)",
                                    flexShrink: 0,
                                    mb: `${TRAY_GAP_PX}px`,
                                }}
                            >
                                <ScreenshotBoard
                                    grid={grid}
                                    imageUrl={imageUrl}
                                    paths={paths}
                                    svgPathFallback={svgPathFallback}
                                    boardPieceIndices={exportLite ? [] : boardPieceIndices}
                                    matPreset={boardMat}
                                    matColor={boardMat.hex}
                                    grooveStyle={boardGrooveStyle}
                                    showDieLines
                                    showFullImage={exportLite}
                                    fillHeight
                                    coverViewport={false}
                                    pieceBevel3d={false}
                                    onScaleChange={setBoardScale}
                                />
                            </Box>

                            <Box
                                sx={{
                                    width: "100%",
                                    borderRadius: 2,
                                    bgcolor: "rgba(255,255,255,0.32)",
                                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.4)",
                                    overflow: "hidden",
                                    py: 0.75,
                                    flexShrink: 0,
                                    // Avoid N× full-size SVG <image> copies during export (blows past SVG URL limits).
                                    visibility: exportLite ? "hidden" : "visible",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        flexWrap: "wrap",
                                        alignContent: "flex-start",
                                        alignItems: "center",
                                        width: "100%",
                                        height: trayCirclePx * 2 + TRAY_ROW_GAP_PX,
                                        px: 1,
                                        columnGap: 0.5,
                                        rowGap: `${TRAY_ROW_GAP_PX}px`,
                                        overflow: "hidden",
                                    }}
                                >
                                    {(exportLite ? [] : trayPieceIndices.slice(0, 16)).map((pieceIndex) => (
                                        <Box
                                            key={pieceIndex}
                                            sx={{
                                                flexShrink: 0,
                                                width: trayCirclePx,
                                                height: trayCirclePx,
                                                borderRadius: "50%",
                                                overflow: "hidden",
                                                bgcolor: "rgba(255,255,255,0.55)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 4px 12px rgba(15,23,42,0.14)",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    transform: `scale(${boardScale})`,
                                                    transformOrigin: "center center",
                                                    lineHeight: 0,
                                                }}
                                            >
                                                <PieceVisual
                                                    pieceIndex={pieceIndex}
                                                    imageUrl={imageUrl}
                                                    grid={grid}
                                                    paths={paths}
                                                    showGridCoords={false}
                                                    anchorToCell={false}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={48} sx={{ color: "#3BA666" }} />
                    </Box>
                )}
            </Box>

            <CardFooter appIcon={JIGSAW_APP_ICON} label="Jigsaw Muse" />
        </Box>
    );
}

/**
 * Live Word Wheel Quest play surface — grid + letter wheel from screenshot play panel.
 */
function WordWheelPlayCard({ puzzle, scene, foundWords }) {
    const ready = Boolean(puzzle);

    return (
        <Box
            sx={{
                flex: "1 1 0",
                minWidth: 0,
                height: "100%",
                borderRadius: "28px",
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 24px 64px rgba(15, 23, 42, 0.28)",
                border: "4px solid rgba(255,255,255,0.92)",
                bgcolor: "#0c4a6e",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
            }}
        >
            <Typography
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    m: 0,
                    pt: "120px",
                    px: 2.5,
                    maxWidth: "100%",
                    fontFamily: BRAND_STACK,
                    fontWeight: 800,
                    fontSize: 34,
                    lineHeight: 1.15,
                    letterSpacing: "0.01em",
                    color: "#F8FAFC",
                    textAlign: "center",
                    textShadow: "0 2px 12px rgba(2,6,23,0.55)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    boxSizing: "border-box",
                }}
            >
                WORD WHEEL QUEST
            </Typography>

            <Box
                sx={{
                    flex: "1 1 0",
                    minHeight: 0,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    zIndex: 1,
                    // Soften ScreenshotPlayPanel top inset so title + play fit the card.
                    "& > *": {
                        flex: 1,
                        minHeight: 0,
                        height: "100% !important",
                        borderRadius: "0 !important",
                    },
                }}
            >
                {ready ? (
                    <ScreenshotPlayPanel
                        puzzle={puzzle}
                        foundWords={foundWords}
                        journeyLevel={scene?.journeyLevel}
                        background={scene?.background || "deep_sea"}
                        theme={scene?.theme || "random"}
                        fill
                        topInset
                        demoDragWord="GUT"
                    />
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={48} sx={{ color: "#38bdf8" }} />
                    </Box>
                )}
            </Box>

            <CardFooter appIcon={WORD_WHEEL_APP_ICON} label="Word Wheel Quest" />
        </Box>
    );
}

/**
 * Google Play developer header capture — 4096×2304.
 * Left: company name · Right: Sliding / Jigsaw Muse play board / Word Wheel Quest.
 */
export default function CompanyGoogleHeaderPage() {
    const [previewScale, setPreviewScale] = useState(0.2);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [jigsawPuzzle, setJigsawPuzzle] = useState({
        imageUrl: null,
        grid: 0,
        paths: null,
        svgPathFallback: null,
        boardPieceIndices: [],
        trayPieceIndices: [],
        puzzleTitle: "",
        ready: false,
    });
    const [wordWheel, setWordWheel] = useState({
        puzzle: null,
        scene: null,
        foundWords: [],
        ready: false,
    });
    const [exportLite, setExportLite] = useState(false);
    const frameRef = useRef(null);

    useEffect(() => {
        const scene = getScreenshotScene(JIGSAW_PLAY_SCENE_SLUG);
        if (!scene?.storyId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { group, template } = await loadScreenshotJigsaw(scene);
                if (cancelled) return;
                const imageUrl = getImageUrl(group?.story);
                const grid = getTemplateGrid(template) || 0;
                const paths = template?.piecePaths?.pieces ?? template?.piecePaths;
                const total = grid * grid;
                // In-progress board: keep some empty so die-line grid + tray pieces show.
                const boardPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
                    (i) => i % 5 !== 0 && i % 7 !== 0,
                );
                const onBoard = new Set(boardPieceIndices);
                const trayPieceIndices = Array.from({ length: total }, (_, i) => i).filter(
                    (i) => !onBoard.has(i),
                );
                setJigsawPuzzle({
                    imageUrl,
                    grid,
                    paths,
                    svgPathFallback: template?.svgPath ?? null,
                    boardPieceIndices,
                    trayPieceIndices,
                    puzzleTitle: getStoryTitle(group?.story),
                    ready: Boolean(imageUrl && grid > 0 && paths),
                });
            } catch {
                if (!cancelled) {
                    setJigsawPuzzle((prev) => ({ ...prev, ready: false }));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const scene = getWwScreenshotScene(WORD_WHEEL_PLAY_SCENE_SLUG);
        if (!scene?.journeyLevel) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const puzzle = await loadScreenshotWordWheel(scene);
                if (cancelled) return;
                const words = (puzzle?.wordsInUse || "")
                    .split("\n")
                    .map((w) => normalizeWord(w))
                    .filter(Boolean);
                const count = Math.max(0, Number(scene.demoFoundWordCount) || 0);
                const foundWords = words.slice(0, Math.min(count, words.length));
                setWordWheel({
                    puzzle,
                    scene,
                    foundWords,
                    ready: Boolean(puzzle),
                });
            } catch {
                if (!cancelled) {
                    setWordWheel((prev) => ({ ...prev, ready: false }));
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const updateScale = () => {
            const padding = 40;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / GOOGLE_HEADER_W, availableH / GOOGLE_HEADER_H),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleDownloadJpeg = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        // html-to-image mis-captures when the frame is CSS-scaled for preview.
        node.style.transform = "none";
        // Collapse many SVG piece <image> copies → one full board image for export.
        setExportLite(true);
        try {
            // Wait until React commits the lite board (one <image>, no tray pieces).
            for (let i = 0; i < 40; i += 1) {
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve)),
                );
                const pieceSvgs = node.querySelectorAll(
                    "[data-jigsaw-play-card] image[href], [data-jigsaw-play-card] image",
                );
                // Lite mode: board uses showFullImage → typically 1 image; tray empty.
                if (pieceSvgs.length > 0 && pieceSvgs.length <= 3) break;
                await new Promise((resolve) => setTimeout(resolve, 30));
            }

            // Ensure the single board <image> is embeddable (CORS-safe data URL).
            const svgImages = node.querySelectorAll("image");
            await Promise.all(
                [...svgImages].map(async (el) => {
                    const href = el.getAttribute("href") || el.href?.baseVal || "";
                    if (!href || href.startsWith("data:")) return;
                    try {
                        const dataUrl = await toEmbeddableDataUrl(href);
                        el.setAttribute("href", dataUrl);
                        if (el.href) el.href.baseVal = dataUrl;
                    } catch {
                        /* onImageErrorHandler covers leftovers */
                    }
                }),
            );
            const htmlImages = node.querySelectorAll("img");
            await Promise.all(
                [...htmlImages].map(async (el) => {
                    const href = el.currentSrc || el.src || "";
                    if (!href || href.startsWith("data:") || href.startsWith("blob:")) return;
                    // Same-origin public assets are fine; skip converting huge local icons unless needed.
                    if (href.startsWith(window.location.origin) || href.startsWith("/")) return;
                    try {
                        el.src = await toEmbeddableDataUrl(href);
                    } catch {
                        /* ignore */
                    }
                }),
            );

            const captureOpts = (quality) => ({
                width: GOOGLE_HEADER_W,
                height: GOOGLE_HEADER_H,
                pixelRatio: 1,
                quality,
                cacheBust: true,
                skipFonts: true,
                backgroundColor: "#E8ECF1",
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
                onImageErrorHandler: () => undefined,
                onclone: (clonedDoc) => {
                    clonedDoc.querySelectorAll("*").forEach((el) => {
                        if (!(el instanceof HTMLElement) && !(el instanceof SVGElement)) return;
                        try {
                            el.style.animation = "none";
                            el.style.transition = "none";
                        } catch {
                            /* ignore */
                        }
                    });
                },
            });
            // Opaque JPEG — Google requires non-transparent 24-bit image, ≤1 MB.
            let quality = 0.82;
            let dataUrl = await toJpeg(node, captureOpts(quality));
            let blob = await (await fetch(dataUrl)).blob();
            while (blob.size > 1024 * 1024 && quality > 0.45) {
                quality -= 0.08;
                dataUrl = await toJpeg(node, captureOpts(quality));
                blob = await (await fetch(dataUrl)).blob();
            }
            if (blob.size > 1024 * 1024) {
                setSaveError(
                    `Export is ${(blob.size / 1024 / 1024).toFixed(2)} MB (limit 1 MB). Try again.`,
                );
                return;
            }
            downloadBlob(blob, GOOGLE_HEADER_FILE);
        } catch (e) {
            setSaveError(formatExportError(e));
        } finally {
            node.style.transform = previousTransform;
            setExportLite(false);
            setSaving(false);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#F1F5F9",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(GOOGLE_HEADER_W * previewScale),
                    height: Math.round(GOOGLE_HEADER_H * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 12px 40px rgba(15, 23, 42, 0.14)",
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="company-google-header"
                    sx={{
                        width: GOOGLE_HEADER_W,
                        height: GOOGLE_HEADER_H,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#E8ECF1",
                        backgroundImage: `
                            radial-gradient(ellipse 70% 80% at 18% 50%, #F8FAFC 0%, transparent 55%),
                            radial-gradient(ellipse 55% 70% at 78% 42%, #DCE7F5 0%, transparent 60%),
                            linear-gradient(135deg, #F4F6F8 0%, #E2E8F0 48%, #CBD5E1 100%)
                        `,
                        boxSizing: "border-box",
                    }}
                >
                    {/* LEFT — company name */}
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "42%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            pl: 14,
                            pr: 6,
                            boxSizing: "border-box",
                            zIndex: 5,
                        }}
                    >
                        <Typography
                            sx={{
                                m: 0,
                                mb: 2,
                                fontFamily: BRAND_STACK,
                                fontWeight: 700,
                                fontSize: 36,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "#64748B",
                            }}
                        >
                            Multi-genre puzzle studio
                        </Typography>
                        <Typography
                            component="h1"
                            sx={{
                                m: 0,
                                fontFamily: BRAND_STACK,
                                fontWeight: 900,
                                fontSize: 168,
                                lineHeight: 0.92,
                                letterSpacing: "-0.03em",
                                color: "#0F172A",
                            }}
                        >
                            Puzzle
                            <Box component="span" sx={{ display: "block", color: "#0369A1" }}>
                                Interact
                            </Box>
                        </Typography>
                        <Typography
                            sx={{
                                mt: 5,
                                maxWidth: 980,
                                fontFamily: BRAND_STACK,
                                fontWeight: 500,
                                fontSize: 44,
                                lineHeight: 1.35,
                                color: "#475569",
                            }}
                        >
                            We create engaging puzzle experiences across multiple genres to{" "}
                            <Box component="span" sx={{ color: "#0369A1", fontWeight: 700 }}>
                                challenge
                            </Box>{" "}
                            minds,{" "}
                            <Box component="span" sx={{ color: "#0369A1", fontWeight: 700 }}>
                                spark
                            </Box>{" "}
                            curiosity, and{" "}
                            <Box component="span" sx={{ color: "#0369A1", fontWeight: 700 }}>
                                bring joy
                            </Box>{" "}
                            to players.
                        </Typography>
                    </Box>

                    {/* RIGHT — app cards */}
                    <Box
                        sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: "58%",
                            display: "flex",
                            alignItems: "center",
                            pr: 12,
                            pl: 4,
                            boxSizing: "border-box",
                            zIndex: 4,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={4}
                            sx={{
                                width: "100%",
                                height: "72%",
                                maxHeight: 1680,
                            }}
                        >
                            <GenreCard {...IMAGE_CARDS[0]} />
                            <JigsawMusePlayCard puzzle={jigsawPuzzle} exportLite={exportLite} />
                            <WordWheelPlayCard
                                puzzle={wordWheel.puzzle}
                                scene={wordWheel.scene}
                                foundWords={wordWheel.foundWords}
                            />
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    minHeight: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    py: 1.25,
                    bgcolor: "rgba(255, 255, 255, 0.96)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.35)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
                    Company — Google Header Image · {GOOGLE_HEADER_W}×{GOOGLE_HEADER_H} · JPEG · max
                    1 MB
                </Typography>
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                    Shown at the top of your Google Play developer page
                </Typography>
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleDownloadJpeg}
                    disabled={saving || !jigsawPuzzle.ready}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    Download JPEG
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
